import * as React from 'react';
import { SelectCheckbox } from './SelectCheckbox';
import { useSelectOpen } from './SelectContext';
import type { SelectItemRenderer, SelectOption } from './types';
import { VirtualSelectList } from './VirtualSelectList';

interface FlatSelectContentProps<ItemType extends string> {
  options: SelectOption<ItemType>[];
  selectedItems: ItemType[];
  onToggle: (value: ItemType) => void;
  renderItem?: SelectItemRenderer<ItemType>;
  emptyState?: React.ReactNode;
  selectId?: string;
  selectionMode?: 'single' | 'multiple';
}

export function FlatSelectContent<ItemType extends string>({
  options,
  selectedItems,
  onToggle,
  renderItem = undefined,
  emptyState = undefined,
  selectId = undefined,
  selectionMode = 'multiple',
}: FlatSelectContentProps<ItemType>) {
  const open = useSelectOpen();
  const wasOpen = React.useRef(false);
  const [orderedOptions, setOrderedOptions] = React.useState(options);
  const selectedValues = React.useMemo(() => new Set(selectedItems), [selectedItems]);

  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setOrderedOptions(
        [...options].sort(
          (left, right) => Number(selectedValues.has(right.value!)) - Number(selectedValues.has(left.value!))
        )
      );
    }
    wasOpen.current = open;
  }, [open, options, selectedValues]);

  React.useEffect(() => {
    setOrderedOptions(current => {
      const available = new Map(options.map(option => [option.value, option]));
      const ordered = current
        .map(option => available.get(option.value))
        .filter((option): option is SelectOption<ItemType> => option !== undefined);
      const currentValues = new Set(ordered.map(option => option.value));

      return [...ordered, ...options.filter(option => !currentValues.has(option.value))];
    });
  }, [options]);

  const selectableOptions = React.useMemo(
    () => orderedOptions.filter(option => option.value !== undefined),
    [orderedOptions]
  );

  return (
    <>
      <VirtualSelectList
        items={selectableOptions}
        getKey={option => option.value!}
        getLabel={option => String(option.label ?? '')}
        resetKey={options}
        renderItem={(option, index, tabIndex) => {
          const value = option.value;
          if (value === undefined) return null;
          const selected = selectedValues.has(value);

          return (
            <div
              data-virtual-focus
              data-testid={selectId ? `${selectId}-option-${value}` : 'select-option'}
              className={
                renderItem
                  ? undefined
                  : 'flex h-12 min-h-12 w-full cursor-pointer items-center gap-2 px-3 py-4 text-left text-sm hover:bg-snow-100 aria-selected:bg-snow-200'
              }
              role="option"
              aria-selected={selected}
              aria-posinset={index + 1}
              aria-setsize={selectableOptions.length}
              tabIndex={tabIndex}
              onClick={() => onToggle(value)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggle(value);
                }
              }}
            >
              {renderItem ? (
                renderItem({ option, selected, index })
              ) : (
                <>
                  {selectionMode === 'multiple' ? (
                    <span className="pointer-events-none" aria-hidden="true">
                      <SelectCheckbox checked={selected} />
                    </span>
                  ) : (
                    <span className="flex h-5 w-5 items-center justify-center">
                      {selected && <span className="text-base font-semibold">✓</span>}
                    </span>
                  )}
                  {option.logo && <img src={option.logo} alt="" className="h-6 w-6 object-contain" loading="lazy" />}
                  <span>{option.label}</span>
                </>
              )}
            </div>
          );
        }}
      />
      {emptyState}
    </>
  );
}
